import {
  IconButton,
  IIconProps,
  Label,
  Toggle,
  Text,
  MessageBarType,
  Spinner,
  SearchBox,
  Stack,
  getTheme,
  FontWeights,
  Dropdown,
  IDropdownOption
} from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import { MedicalServiceSpecialty } from '../../../models/Entities/MedicalServices/MedicalServiceSpecialty';
import { MessageComponent } from '../../../commons/component/MessageComponent';

// functions
import * as ComponentFunctions from './MedicalService.Function';
////i18Next
import { useTranslation, Trans } from 'react-i18next';

// Styles
import './MedicalServiceListComponent.css';
import MedicalServiceSettingsFormComponent from '../MedicalServiceSettingsForm/MedicalServiceSettingsFormComponent';

const theme = getTheme();
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';

const MedicalServiceListComponent = (props: ComponentFunctions.Props) => {
  const EditIcon: IIconProps = { iconName: 'Edit' };
  const AddIcon: IIconProps = { iconName: 'CirclePlus' };
  const SettingsIcon: IIconProps = { iconName: 'Settings' };
  //useTranslation()
  const [t] = useTranslation();

  const [
    isSettingsFormOpen,
    { setTrue: openSettingsFormPanel, setFalse: dismissSettingsFormPanel }
  ] = useBoolean(false);

  // state variables
  const [selectedMedicalService, setSelectedMedicalService] = useState<
    AvailableMedicalService | undefined
  >(undefined);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);

  //events
  const onEditEvent = (service: AvailableMedicalService) => {
    if (props.editEvent) props.editEvent(service);
  };

  const onChangeSettings = (service: AvailableMedicalService) => {
    setSelectedMedicalService(service);
    openSettingsFormPanel();
  };

  const onDeletingMedicalService = (service: AvailableMedicalService) => {
    props.DeleteMedicalService(service.id);
  };

  const onChangeStatus = (
    service: AvailableMedicalService,
    enabled: boolean
  ): void => {
    props.ChangeMedicalServiceStatus(service.id, enabled);
  };

  //effects
  useEffect(() => {
    props.GetAllMedicalServices(1, 1000, undefined);
  }, []);

  const specialtyOptions = useMemo((): IDropdownOption[] => {
    if (!props.MedicalServices) return [];
    const seen = new Set<string>();
    const options: IDropdownOption[] = [];
    for (const svc of props.MedicalServices) {
      for (const sp of svc.specialties ?? []) {
        const name = sp.specialtyName ?? String(sp.specialtyId);
        if (!seen.has(name)) {
          seen.add(name);
          options.push({ key: name, text: name });
        }
      }
    }
    return options.sort((a, b) => String(a.text).localeCompare(String(b.text)));
  }, [props.MedicalServices]);

  const filteredServices = props.MedicalServices?.filter((item: AvailableMedicalService) => {
    const matchesName =
      searchText === ''
        ? true
        : item.serviceNameReference.toLocaleLowerCase().includes(searchText.toLocaleLowerCase());
    const matchesSpecialty =
      selectedSpecialties.length === 0
        ? true
        : item.specialties?.some(s =>
            selectedSpecialties.includes(s.specialtyName ?? String(s.specialtyId))
          ) ?? false;
    return matchesName && matchesSpecialty;
  });

  return (
    <>
      {!props.isLoading && props.MedicalServices?.length === 0 && (
        <MessageComponent
          message={t('medicalServiceList.props.registeredMedicalServices')}
          type={MessageBarType.info}
        />
      )}
      {props.isLoadingSuccessfully && (
        <>
          <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginBottom: 12, flexWrap: 'wrap' } }}>
            <Stack.Item grow={1} styles={{ root: { minWidth: 200, maxWidth: 360 } }}>
              <SearchBox
                placeholder={t('medicalServiceList.props.filterByServiceName')}
                value={searchText}
                onChange={(_, val) => setSearchText(val ?? '')}
                onClear={() => setSearchText('')}
                underlined={false}
              />
            </Stack.Item>
            {process.env.REACT_APP_PREVIEW_FEATURES == 'true' && <Stack.Item grow={1} styles={{ root: { minWidth: 200, maxWidth: 360 } }}>
              <Dropdown
                placeholder={t('medicalServiceList.props.filterBySpecialty')}
                multiSelect
                options={specialtyOptions}
                selectedKeys={selectedSpecialties}
                onChange={(_, option) => {
                  if (!option) return;
                  setSelectedSpecialties(prev =>
                    option.selected
                      ? [...prev, option.key as string]
                      : prev.filter(k => k !== option.key)
                  );
                }}
                styles={{ root: { width: '100%' } }}
              />
            </Stack.Item>}
          </Stack>
          <table className="Table">
            <thead>
              <tr>
                <th>{t('medicalServiceList.props.id')}</th>
                <th>{t('medicalServiceList.props.medicalPractice')}</th>
                <th>{t('medicalServiceList.props.snowmedCode')}</th>
                <th>{t('medicalServiceList.props.specialtiesColumn')}</th>
                <th>{t('medicalServiceList.props.state')}</th>
                <th style={{ width: '150px' }}>{t('medicalServiceList.props.state')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.map((item: AvailableMedicalService) => (
                <tr key={item.id} className="TableBodyRow">
                  <td className="primary">{item.id}</td>
                  <td>
                    <Label className="upper">{item.serviceNameReference}</Label>
                    <Text>{item.comments}</Text>
                  </td>
                  <td>
                    <Text>
                      {item.isLinkedToSnowmed &&
                      item.snowmedCode &&
                      item.snowmedName
                        ? item.snowmedCode + ' | ' + item.snowmedName
                        : t('medicalServiceList.props.notLinked')}
                    </Text>
                  </td>
                  <td>
                    {item.specialties && item.specialties.length > 0 ? (
                      <Stack horizontal wrap tokens={{ childrenGap: 4 }}>
                        {item.specialties.map((s: MedicalServiceSpecialty) => (
                          <span
                            key={s.id}
                            style={{
                              backgroundColor: theme.palette.themeLighter,
                              border: `1px solid ${theme.palette.themeLight}`,
                              borderRadius: 12,
                              padding: '2px 10px',
                              fontSize: 12,
                              color: theme.palette.themeDark,
                              fontWeight: FontWeights.semibold as number,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {s.specialtyName ?? String(s.specialtyId)}
                          </span>
                        ))}
                      </Stack>
                    ) : (
                      <Text
                        variant="small"
                        styles={{ root: { color: theme.palette.neutralSecondary, fontStyle: 'italic' } }}
                      >
                        {t('medicalServiceList.props.noSpecialties')}
                      </Text>
                    )}
                  </td>
                  <td>
                    <Toggle
                      label=""
                      defaultChecked={item.available as boolean}
                      onText={t('medicalServiceList.props.enable')}
                      offText={t('medicalServiceList.props.disabled')}
                      checked={item.available as boolean}
                      onChanged={(checked: boolean) =>
                        onChangeStatus(item, checked)
                      }
                    />
                  </td>
                  <td style={{ width: '150px' }}>
                    <IconButton
                      iconProps={EditIcon}
                      title={t('medicalServiceList.props.modify')} 
                      ariaLabel="Edit"
                      onClick={() => onEditEvent(item)}
                    />
                    <IconButton
                      iconProps={SettingsIcon}
                      title={t('medicalServiceList.props.config')}
                      ariaLabel="Settings"
                      onClick={() => onChangeSettings(item)}
                    />
                    <div className="danger">
                      <ConfirmationButtonComponent
                        ButtonId={'MedicalServiceDeleteButton'}
                        ButtonIconName={'Delete'}
                        ButtonText={''}
                        ButtonCssClass={''}
                        ConfirmationButtonText={t('medicalServiceList.confirmationButton.deleteButton')}
                        ConfirmationButtonCssClass={'deleteButton'}
                        CancelButtonText={t('medicalServiceList.confirmationButton.cancelButton')}
                        CancelButtonCssClass={''}
                        Title={t('medicalServiceList.confirmationButton.deleteInformation')}
                        Description={t('medicalServiceList.confirmationButton.descriptionInformation')}
                        ElementTextHeader={t('medicalServiceList.confirmationButton.medicalServiceText')}
                        ElementTextInformation={item.serviceNameReference}
                        onConfirmation={() => onDeletingMedicalService(item)}
                        onCancel={function (): void {
                          throw new Error('Function not implemented.');
                        }}
                        isOperationExecuting={props.isDeleting}
                        isOperationExecutedWithError={
                          props.failOnDeleting || false
                        }
                        OperationError={props.error}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {props.isLoading && (
        <div>
          <Spinner
            label={t('medicalServiceList.props.loadingMedicalServices')} 
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}
      {selectedMedicalService && (
        <MedicalServiceSettingsFormComponent
          isOpen={isSettingsFormOpen}
          openPanel={openSettingsFormPanel}
          dismissPanel={dismissSettingsFormPanel}
          MedicalServiceId={selectedMedicalService.id}
        />
      )}
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(MedicalServiceListComponent as any);
